import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2, CalendarDays, Gift } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Reveal, MaskedLine } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  from_address: "",
  to_address: "",
  date: "",
  move_type: "Student med utplacering",
  bohag: "Studentrum (15–25 m²)",
  presentkort: "",
  message: "",
};

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3.5 text-sm text-[#0b0f19] placeholder:text-slate-400 focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/15 transition-[border-color,box-shadow] duration-300";

const Field = ({ label, children, testid }) => (
  <label className="flex flex-col gap-2">
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid={testid}>{label}</span>
    {children}
  </label>
);

export const BookingQuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [dateObj, setDateObj] = useState(undefined);
  const [calOpen, setCalOpen] = useState(false);

  const hasGiftcard = form.move_type === "Student med utplacering";

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const setMoveType = (e) => {
    const v = e.target.value;
    setForm({ ...form, move_type: v, presentkort: v === "Student med utplacering" ? form.presentkort : "" });
  };

  const pickDate = (d) => {
    setDateObj(d);
    setForm({ ...form, date: d ? format(d, "yyyy-MM-dd") : "" });
    if (d) setCalOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) {
      toast.error("Välj ett flyttdatum i kalendern.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/quotes`, form);
      setStatus("sent");
      toast.success("Tack! Din offertförfrågan är skickad – vi hör av oss så snart vi kan.");
      setForm(INITIAL);
      setDateObj(undefined);
    } catch (err) {
      setStatus("idle");
      toast.error("Något gick fel. Försök igen eller ring oss på 070-727 29 41.");
    }
  };

  return (
    <section id="offert" data-testid="quote-form-section" className="relative bg-[#fafafa] py-24 sm:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1fr_1.25fr] gap-14 lg:gap-20">
        <div className="lg:sticky lg:top-28 self-start text-center lg:text-left">
          <Reveal>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#16a34a] mb-5">Offertförfrågan</p>
          </Reveal>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.05] text-[clamp(1.9rem,8vw,3.75rem)] text-[#0b0f19]">
            <MaskedLine>Berätta om</MaskedLine>
            <MaskedLine delay={0.1}>din flytt – vi</MaskedLine>
            <MaskedLine delay={0.2}><span className="text-[#16a34a]">hör av oss.</span></MaskedLine>
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-6 text-base leading-relaxed text-slate-600 max-w-md mx-auto lg:mx-0">
              Fyll i formuläret så återkommer vi med fast pris anpassat till din flytt – och
              bidragsunderlag om du flyttar via universitetet.
            </p>
            <div className="mt-8 flex flex-col gap-3 text-sm text-slate-600 items-center lg:items-start">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" /> Fast pris i förväg – inga överraskningar
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" /> Faktura direkt till universitetet vid utplacering
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" /> 30 % RUT-avdrag för privatpersoner
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-10 shadow-sm grid sm:grid-cols-2 gap-5"
          >
            <Field label="Typ av flytt" testid="label-move-type">
              <select data-testid="select-move-type" value={form.move_type} onChange={setMoveType} className={inputCls}>
                <option>Student med utplacering</option>
                <option>Student</option>
                <option>Privatperson / Familj</option>
              </select>
            </Field>
            <Field label="Bohag / storlek" testid="label-bohag">
              <select data-testid="select-bohag" value={form.bohag} onChange={set("bohag")} className={inputCls}>
                <option>Studentrum (15–25 m²)</option>
                <option>1 rum & kök (25–40 m²)</option>
                <option>2–3 rum & kök (45–80 m²)</option>
                <option>Större bohag</option>
                <option>Endast enstaka möbler</option>
              </select>
            </Field>
            <Field label="Namn" testid="label-name">
              <input data-testid="input-name" required value={form.name} onChange={set("name")} placeholder="För- och efternamn" className={inputCls} />
            </Field>
            <Field label="Telefonnummer" testid="label-phone">
              <input data-testid="input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="070-123 45 67" className={inputCls} />
            </Field>
            <Field label="E-post" testid="label-email">
              <input data-testid="input-email" required type="email" value={form.email} onChange={set("email")} placeholder="namn@exempel.se" className={inputCls} />
            </Field>
            <Field label="Önskat flyttdatum" testid="label-date">
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="input-date"
                    className={`${inputCls} flex items-center justify-between text-left ${!dateObj ? "text-slate-400" : ""}`}
                  >
                    {dateObj ? format(dateObj, "d MMMM yyyy", { locale: sv }) : "Välj datum i kalendern"}
                    <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-2xl border-slate-200 shadow-xl" align="start">
                  <Calendar
                    mode="single"
                    selected={dateObj}
                    onSelect={pickDate}
                    disabled={{ before: new Date() }}
                    locale={sv}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Från adress (gata, våning, hiss?)" testid="label-from-address">
              <input data-testid="input-from-address" required value={form.from_address} onChange={set("from_address")} placeholder="T.ex. Storgatan 1, Umeå – vån 3, hiss finns" className={inputCls} />
            </Field>
            <Field label="Till adress (gata, våning, hiss?)" testid="label-to-address">
              <input data-testid="input-to-address" required value={form.to_address} onChange={set("to_address")} placeholder="T.ex. Sjukhuvägen 2, Östersund – vån 1" className={inputCls} />
            </Field>
            <AnimatePresence initial={false}>
              {hasGiftcard && (
                <motion.div
                  key="presentkort"
                  data-testid="giftcard-section"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden sm:col-span-2"
                >
                  <div className="rounded-xl border border-[#16a34a]/30 bg-[#16a34a]/5 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                    <Gift className="w-5 h-5 text-[#16a34a] shrink-0 hidden sm:block" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#0b0f19]">Studentpaket ingår vid utplacering!</p>
                      <p className="text-xs text-slate-500 mt-0.5">Välj ditt presentkort värt 1 000 kr (frivilligt)</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {["IKEA", "Hemtex", "Cervera"].map((brand) => (
                        <button
                          key={brand}
                          type="button"
                          data-testid={`giftcard-${brand.toLowerCase()}`}
                          onClick={() => setForm({ ...form, presentkort: form.presentkort === brand ? "" : brand })}
                          className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                            form.presentkort === brand
                              ? "bg-[#16a34a] border-[#16a34a] text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-[#16a34a]/50"
                          }`}
                        >
                          {brand}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="sm:col-span-2">
              <Field label="Meddelande / speciella önskemål" testid="label-message">
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="T.ex. bärhjälp, piano, flexibelt datum…"
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </div>
            <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 pt-2">
              <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                Genom att skicka godkänner du att vi kontaktar dig om din offert. Kostnadsfritt och utan förpliktelser.{" "}
                <a data-testid="form-privacy-link" href="/integritetspolicy" className="underline hover:text-[#16a34a]">
                  Läs vår integritetspolicy
                </a>
              </p>
              <button
                data-testid="quote-submit-button"
                type="submit"
                disabled={status === "sending"}
                className="group bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-full flex items-center gap-2 transition-colors duration-300 shrink-0"
              >
                {status === "sending" ? (
                  <>Skickar <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>Skicka offertförfrågan <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
